import { useEffect, useRef, useState } from "react";
import type { Band, LightColor, SlotRole, StageConfig, StagePreset } from "../../infra/api.js";
import { DEFAULT_STAGE } from "../../infra/api.js";
import { LIGHT_HEX, POSITIONS } from "../stage/stage-layout.js";
import { LpcAvatar } from "./LpcAvatar.js";

// Re-exported so existing importers of `Stage`'s `LIGHT_HEX` keep working; the
// constant itself now lives in the shared `stage-layout` module (Task 5.1).
export { LIGHT_HEX };

/**
 * Spec §F6: pause stage animations while the stage is off-viewport so a long
 * library page does not burn CPU animating avatars the user cannot see.
 * Returns a ref to attach to the stage root and whether it is on screen.
 * Degrades to "always visible" when IntersectionObserver is unavailable.
 */
function useOnScreen(): {
  ref: React.RefObject<HTMLDivElement>;
  onScreen: boolean;
} {
  const ref = useRef<HTMLDivElement>(null);
  const [onScreen, setOnScreen] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry) setOnScreen(entry.isIntersecting);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, onScreen };
}

interface Props {
  band: Band;
  playing: boolean;
  size?: number;
  height?: number;
  configOverride?: StageConfig;
}

// === Preset backdrops ===
interface PresetDef {
  bg: string;
  floor: string;
  floorTop: string;
  wall?: string;
  extra?: JSX.Element;
}

function preset(p: StagePreset): PresetDef {
  switch (p) {
    case "jazz":
      return {
        bg: "radial-gradient(ellipse 70% 50% at 50% 100%, rgba(80,60,160,0.25) 0%, rgba(0,0,0,0) 70%), linear-gradient(180deg, #0a0a2a 0%, #1a1a4a 60%, #050518 100%)",
        floor: "linear-gradient(180deg, #1a0c2c 0%, #06030f 100%)",
        floorTop: "#3a2058",
        wall: "linear-gradient(180deg, #1a1a3a 0%, #0a0a1a 100%)",
        extra: (
          // stylized art deco curtain folds
          <g>
            {Array.from({ length: 6 }).map((_, i) => (
              <rect key={i} x={i * 17} y="0" width="2" height="55" fill="rgba(180,150,80,0.12)" />
            ))}
          </g>
        ),
      };
    case "openair":
      return {
        bg: "linear-gradient(180deg, #2a1a5a 0%, #6b3a78 35%, #d86a3a 70%, #f0b864 95%, #5a2818 100%)",
        floor: "linear-gradient(180deg, #4a3018 0%, #1a0e08 100%)",
        floorTop: "#7a4a28",
      };
    case "garage":
      return {
        bg: "linear-gradient(180deg, #2a2a30 0%, #3a3a45 100%)",
        floor: "linear-gradient(180deg, #4a4a50 0%, #2a2a30 100%)",
        floorTop: "#5a5a60",
        wall: "repeating-linear-gradient(0deg, rgba(0,0,0,0.15) 0 8px, transparent 8px 16px), linear-gradient(180deg, #4a4a52 0%, #2a2a32 100%)",
        extra: (
          // exposed concrete blocks
          <g>
            {Array.from({ length: 8 }).map((_, i) => (
              <rect
                key={`b-${i}`}
                x={i * 13}
                y={i % 2 ? 18 : 12}
                width="10"
                height="0.5"
                fill="rgba(0,0,0,0.3)"
              />
            ))}
          </g>
        ),
      };
    case "synthwave":
      return {
        bg: "radial-gradient(ellipse 100% 60% at 50% 100%, rgba(240,80,200,0.40) 0%, rgba(0,0,0,0) 70%), linear-gradient(180deg, #0a0028 0%, #2a0a4a 40%, #1a0530 80%, #050015 100%)",
        floor:
          "linear-gradient(180deg, #1a0530 0%, #050015 100%), repeating-linear-gradient(0deg, transparent 0 8px, rgba(240,80,200,0.4) 8px 9px), repeating-linear-gradient(90deg, transparent 0 12px, rgba(120,40,200,0.3) 12px 13px)",
        floorTop: "#e040b8",
        wall: "linear-gradient(180deg, #2a0a4a 0%, #1a0530 100%)",
      };
    case "acoustic":
      return {
        bg: "linear-gradient(180deg, #4a2818 0%, #6a3a20 40%, #8a4a28 70%, #2a1408 100%)",
        floor: "linear-gradient(180deg, #6a3a20 0%, #2a1408 100%)",
        floorTop: "#8a5a30",
        wall: "linear-gradient(180deg, #5a3018 0%, #3a1808 100%)",
        extra: (
          // hanging string lights
          <g>
            <path d="M 0,8 Q 50,18 100,8" fill="none" stroke="#3a2010" strokeWidth="0.4" />
            {[10, 30, 50, 70, 90].map((x, i) => (
              <g key={x}>
                <circle cx={x} cy={11 + (i % 2)} r="1.6" fill="#f0c84a" opacity="0.9" />
                <circle cx={x} cy={11 + (i % 2)} r="2.4" fill="#f0c84a" opacity="0.3" />
              </g>
            ))}
          </g>
        ),
      };
    case "underground":
    default:
      return {
        bg: "radial-gradient(ellipse 60% 60% at 50% 90%, rgba(232,68,58,0.35) 0%, rgba(0,0,0,0) 70%), radial-gradient(ellipse 70% 40% at 50% 20%, rgba(240,200,74,0.18) 0%, rgba(0,0,0,0) 70%), linear-gradient(180deg, #1a120c 0%, #2a1a10 50%, #150f08 100%)",
        floor: "linear-gradient(180deg, #2a1a10 0%, #0a0604 100%)",
        floorTop: "#4a2a18",
      };
  }
}

function EqBars({ playing }: { playing: boolean }): JSX.Element {
  return (
    <div className="absolute right-4 top-4 flex h-8 items-end gap-0.5">
      {Array.from({ length: 16 }).map((_, i) => (
        <span
          key={i}
          className={`block w-[3px] bg-butter ${playing ? "" : "opacity-30"}`}
          style={{
            height: "100%",
            transformOrigin: "bottom",
            animation: playing
              ? `eq-bar ${0.5 + (i % 4) * 0.15}s ease-in-out ${i * 0.05}s infinite`
              : undefined,
            transform: playing ? undefined : "scaleY(0.3)",
          }}
        />
      ))}
    </div>
  );
}

function Smoke(): JSX.Element {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 overflow-hidden">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full opacity-30"
          style={{
            left: `${15 + i * 30}%`,
            bottom: `-${20 + i * 10}%`,
            width: "60%",
            height: "70%",
            background:
              "radial-gradient(circle, rgba(240,200,74,0.20) 0%, rgba(255,255,255,0.10) 30%, rgba(0,0,0,0) 65%)",
            filter: "blur(8px)",
            animation: `smoke-drift ${10 + i * 3}s ease-in-out infinite`,
            animationDelay: `${i * -3}s`,
          }}
        />
      ))}
    </div>
  );
}

function Banner({ text, color }: { text: string; color: LightColor }): JSX.Element {
  const hex = LIGHT_HEX[color];
  return (
    <div className="absolute left-1/2 top-3 -translate-x-1/2">
      <div
        className="font-display tracking-tight"
        style={{
          fontSize: 22,
          fontVariationSettings: '"opsz" 96, "wght" 700, "SOFT" 100, "WONK" 1',
          color: hex,
          textShadow: `0 0 6px ${hex}cc, 0 0 14px ${hex}80, 0 0 28px ${hex}40`,
          fontStyle: "italic",
          letterSpacing: "0.05em",
        }}
      >
        {text}
      </div>
    </div>
  );
}

function Spotlight({
  color,
  position,
  delay = 0,
}: {
  color: LightColor;
  position: "left" | "right";
  delay?: number;
}): JSX.Element {
  const hex = LIGHT_HEX[color];
  const left = position === "left" ? "20%" : "80%";
  return (
    <div
      className="spotlight pointer-events-none absolute -top-12 h-44 w-44 -translate-x-1/2 rounded-full"
      style={{
        left,
        background: `radial-gradient(circle, ${hex}66 0%, ${hex}00 60%)`,
        animationDelay: `${delay}s`,
      }}
    />
  );
}

function AmpStack({ side }: { side: "left" | "right" }): JSX.Element {
  const x = side === "left" ? "4%" : "82%";
  return (
    <div
      className="absolute"
      style={{
        left: x,
        bottom: "22%",
        zIndex: 0,
        width: 50,
      }}
    >
      <svg viewBox="0 0 50 64" width="48" height="60" shapeRendering="crispEdges">
        {/* Top amp */}
        <rect x="2" y="2" width="46" height="22" fill="#1a1410" stroke="#4a3020" strokeWidth="1" />
        <rect x="6" y="6" width="38" height="10" fill="#3a2820" />
        <rect x="6" y="6" width="38" height="2" fill="#5a3830" />
        {/* Speaker grills */}
        <circle cx="14" cy="18" r="2.5" fill="#0a0604" stroke="#3a2010" strokeWidth="0.6" />
        <circle cx="25" cy="18" r="2.5" fill="#0a0604" stroke="#3a2010" strokeWidth="0.6" />
        <circle cx="36" cy="18" r="2.5" fill="#0a0604" stroke="#3a2010" strokeWidth="0.6" />
        {/* Power LED */}
        <circle cx="44" cy="11" r="0.8" fill="#e8443a" />
        {/* Bottom cab */}
        <rect x="2" y="28" width="46" height="34" fill="#1a1410" stroke="#4a3020" strokeWidth="1" />
        <circle cx="15" cy="44" r="6" fill="#0a0604" stroke="#3a2010" strokeWidth="0.6" />
        <circle cx="35" cy="44" r="6" fill="#0a0604" stroke="#3a2010" strokeWidth="0.6" />
        <circle cx="15" cy="44" r="2" fill="#3a2010" />
        <circle cx="35" cy="44" r="2" fill="#3a2010" />
      </svg>
    </div>
  );
}

export function Stage({
  band,
  playing,
  size = 96,
  height = 360,
  configOverride,
}: Props): JSX.Element {
  const cfg = configOverride ?? band.stage_config ?? DEFAULT_STAGE;
  const pdef = preset(cfg.preset);

  // §F6: suppress play animations when the stage scrolls off-viewport.
  const { ref, onScreen } = useOnScreen();
  const active = playing && onScreen;

  return (
    <div
      ref={ref}
      data-paused={playing && !onScreen ? "true" : undefined}
      className="relative w-full overflow-hidden border border-border"
      style={{ height, background: pdef.bg }}
    >
      {/* Backdrop wall (extra layer for presets that have it) */}
      {pdef.wall && (
        <div
          className="absolute inset-x-0 top-0"
          style={{ height: "55%", background: pdef.wall, opacity: 0.8 }}
        />
      )}

      {/* Scanlines */}
      <div className="stage-scanlines pointer-events-none absolute inset-0" />

      {/* Preset extras (decoration layer) */}
      {pdef.extra && (
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox="0 0 100 60"
          preserveAspectRatio="xMidYMin slice"
        >
          {pdef.extra}
        </svg>
      )}

      {/* Spotlights */}
      <Spotlight color={cfg.lightLeft} position="left" delay={0} />
      <Spotlight color={cfg.lightRight} position="right" delay={1.2} />

      {/* Banner */}
      {cfg.bannerText && <Banner text={cfg.bannerText} color={cfg.bannerColor} />}

      {/* Status tag */}
      <div className="absolute left-4 top-4 z-10 flex items-center gap-2">
        <span
          className={`inline-block h-1.5 w-1.5 ${
            playing ? "bg-vinyl animate-pulse" : "bg-ink-dim"
          }`}
        />
        <span className="font-mono text-[10px] uppercase tracking-widest2 text-paper">
          {playing ? "ao vivo" : "ensaio"}
        </span>
      </div>

      <EqBars playing={active} />

      {/* Amp stacks behind guitarist/bassist */}
      {cfg.ampStack && (
        <>
          <AmpStack side="left" />
          <AmpStack side="right" />
        </>
      )}

      {/* Floor */}
      <div
        className="absolute inset-x-0 bottom-0 h-14"
        style={{
          background: pdef.floor,
          borderTop: `1px solid ${pdef.floorTop}`,
          boxShadow: "inset 0 6px 10px -6px rgba(0,0,0,0.8)",
        }}
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              cfg.preset === "synthwave"
                ? "repeating-linear-gradient(90deg, transparent 0, transparent 22px, rgba(240,80,200,0.5) 22px 23px)"
                : "repeating-linear-gradient(90deg, transparent 0, transparent 38px, rgba(0,0,0,0.4) 38px, rgba(0,0,0,0.4) 40px)",
          }}
        />
      </div>

      {/* Drum riser */}
      {cfg.drumRiser && (
        <div
          className="absolute"
          style={{
            left: "32%",
            bottom: "22%",
            width: "36%",
            height: 18,
            background: "linear-gradient(180deg, #3a2418 0%, #1a0e08 100%)",
            borderTop: "1px solid #5a3a20",
            zIndex: 0,
          }}
        />
      )}

      {/* Smoke */}
      {cfg.smoke && <Smoke />}

      {/* Avatars */}
      {(Object.keys(POSITIONS) as SlotRole[]).map((role) => {
        const slot = band.slots.find((s) => s.slot_role === role);
        if (!slot) return null;
        const pos = POSITIONS[role];
        const top = role === "drums" && cfg.drumRiser ? "16%" : pos.top;
        return (
          <div
            key={role}
            className="absolute flex flex-col items-center"
            style={{
              left: pos.left,
              top,
              transform: `translate(-50%, -50%) scale(${pos.scale ?? 1})`,
              zIndex: pos.zIndex,
            }}
          >
            <LpcAvatar
              layers={slot.artist.lpc_layers}
              size={size}
              label={slot.artist.name}
              action={active ? (`play-${role}` as const) : "idle"}
              instrument={role}
            />
            <span className="mt-1 border border-border bg-asphalt/80 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest2 text-paper">
              {slot.artist.name}
            </span>
          </div>
        );
      })}

      {band.slots.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="font-mono text-[10px] uppercase tracking-widest2 text-ink-muted">
            palco vazio — adicione membros aos slots
          </p>
        </div>
      )}
    </div>
  );
}
